import { throttle } from "lodash-es";
import { storeToRefs } from "pinia";
import { Ref, computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { Artefact } from "@/lib/artefacts";
import { getDescriptionArtefact } from "@/lib/artefacts/markdown";
import { api, type types } from "@/lib/openapi";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { defineEntityStore } from "@/stores/helpers";
import { useReleaseStore } from "@/stores/release-store";
import { useSyncStore } from "@/stores/sync-store";

import {
    FILE_SYNC_THROTTLE_MS,
    discardDraftsAfterSync,
    useDraftManager,
} from "./draft-manager";
import { useEditManager } from "./edit-manager";
import { useEntityNameManager } from "./entity-name-manager";
import { registerFlush } from "./pending-flush";
import {
    refreshOnCrossTabEntitySync,
    refreshOnExternalEntityChange,
    wouldClobberLocalChanges,
} from "./refresh-on-entity-change";
import { clearStaleContentOnSwitch } from "./stale-while-revalidate";
import { isAbortError, useLatestRequest } from "./use-latest-request";

export type Product = types.Product;

// Uses the current route to determine what the 'product' should be and fetches
// the product description for you.
export const useProductStore = defineEntityStore("product", () => {
    const { organization } = useOrganizationManager();
    const { stek } = useStekManager();

    const product = ref<Product | undefined>(undefined);
    const router = useRouter();
    const syncStore = useSyncStore();
    const entityId = computed(() => product.value?.identity);
    const description: Ref<Artefact<"text/markdown"> | undefined> = ref();
    const descriptionDraft = useDraftManager({
        entityId,
        entityType: EntityType.PRODUCT,
        attribute: "description",
        artefact: description,
    });
    discardDraftsAfterSync(entityId, [descriptionDraft], EntityType.PRODUCT);

    const { product: productInfo } = useCurrentEntityManager();
    const releaseStore = useReleaseStore();
    const revisionQuery = computed(() =>
        releaseStore.activeRevision
            ? new URLSearchParams({ revision: releaseStore.activeRevision })
            : undefined
    );

    const isFetching = ref(false);
    const isFetchingDescription = ref(false);

    // Latest-request guard: each fetchProduct gets its own AbortController +
    // token, so a stale run can neither abort the current one nor commit results.
    const { begin, isCurrent } = useLatestRequest();
    const {
        isModified: isNameModified,
        reset: resetName,
        persist: persistName,
    } = useEntityNameManager(product);

    async function fetchProduct(force = false) {
        const productId = productInfo.value?.identity;

        // In case the same product is selected, return early.
        if (!force && product.value && product.value.identity === productId) {
            return;
        }

        // Start a new request: aborts the previous one, our own signal + token.
        const { token, signal } = begin();

        if (!productId) {
            description.value = undefined;
            product.value = undefined;
            return;
        }

        isFetching.value = true;
        // Stale-while-revalidate: keep the previously-loaded product + description
        // on screen while the *same* product is refetched, so it doesn't blank the
        // page (the deferred spinner then never flashes). The data overwrites
        // below; fetchDescription always refetches for the current product.
        // Switching to a *different* product is handled by clearStaleContentOnSwitch.

        try {
            const { data, response } =
                await api.startProductProductMgtGetProductStartPost({
                    body: {
                        product_id: `${productId}`,
                    },
                    signal,
                });

            // A newer request superseded us while fetching — commit nothing.
            if (!isCurrent(token)) {
                return;
            }

            if (!response.ok) {
                if (response.status === 404) {
                    router.replace({
                        name: "not-found",
                        params: {
                            orgName: organization.value?.name,
                            stekName: stek.value?.name,
                        },
                        query: {
                            type: "product",
                            id: productId,
                        },
                    });
                    return;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Landing-time clobber guard: edits may have appeared while this
            // GET was in flight — the notification-time guard can't see
            // them. Only a same-product refetch is dropped; navigating to a
            // different product must always commit. A dropped commit
            // self-heals: the edit's own sync re-notifies and refetches.
            if (
                productId === product.value?.identity &&
                wouldClobberLocalChanges(externalRefresh)
            ) {
                return;
            }
            product.value = data;
            // The fetched row is the new name baseline. Without this, a
            // server-side rename surfacing on a same-product refetch would
            // read as a local edit (the name manager only re-baselines on
            // identity change) and get echoed back as a save.
            persistName();

            // Fetch description on the same signal/token so an abort or a newer
            // request supersedes it too, and isFetching stays true until it's done.
            if (product.value) {
                await fetchDescription(signal, token);
            }
        } catch (e) {
            if (isAbortError(e)) {
                return;
            }
            throw e;
        } finally {
            // Only the current request owns the fetch flag.
            if (isCurrent(token)) {
                isFetching.value = false;
            }
        }
    }
    // Drop the previous product's content when the route switches to a different
    // product, so stale-while-revalidate never renders the old one on the new page.
    clearStaleContentOnSwitch(
        () => productInfo.value?.identity,
        [product, description]
    );
    watch(
        () => productInfo.value?.identity,
        () => fetchProduct(),
        { immediate: true }
    );
    watch(
        () => releaseStore.activeRevision,
        () => {
            if (product.value) fetchProduct(true);
        }
    );

    const fetchDescription = async (signal: AbortSignal, token: number) => {
        // Always refetch for the (possibly new) current product — under SWR the
        // previous description stays set, so a `!description.value` guard would
        // wrongly keep the stale value.
        if (product.value) {
            isFetchingDescription.value = true;
            try {
                const descriptionResult = await getDescriptionArtefact(
                    product.value,
                    "productDescription",
                    signal,
                    revisionQuery.value
                );

                // A newer request superseded us — commit nothing. Same for
                // edits that appeared while fetching (landing-time guard).
                if (
                    !isCurrent(token) ||
                    wouldClobberLocalChanges(externalRefresh)
                ) {
                    return;
                }

                description.value = descriptionResult;
            } catch (e) {
                if (isAbortError(e)) {
                    return;
                }
                throw e;
            } finally {
                if (isCurrent(token)) {
                    isFetchingDescription.value = false;
                }
            }
        }
    };

    // No isFetching short-circuit here: edits made WHILE a refetch is in
    // flight must read as modified, or the landing-time clobber guard can't
    // see them and the refetch's response overwrites the user's text.
    const isModified = computed(() =>
        Boolean(isNameModified.value || description.value?.isModified)
    );

    // One shared opts object: the notification guards and the landing-time
    // commit gates inside the update functions must agree on what "would
    // clobber local changes".
    const externalRefresh = {
        entityType: EntityType.PRODUCT,
        currentEntityId: () => product.value?.identity,
        isDirty: () => isModified.value,
        refetch: () => fetchProduct(true),
    };

    // Refresh on an external content change to THIS product (thread catch-up /
    // conflict resolution mutating it at the same revision) — guarded so an
    // in-progress local edit is never clobbered.
    refreshOnExternalEntityChange(externalRefresh);

    const queueSave = async () => {
        if (!isModified.value || !product.value) {
            return;
        }

        const updateContent: types.StartProductProductMgtUpdateProductStartPostData =
            {
                body: {
                    product_id: product.value.identity,
                },
                url: "/product/product_mgt/update_product/start",
            };

        if (isNameModified.value) {
            updateContent.body.name = product.value.name;
        }
        if (description.value?.isModified) {
            updateContent.body.description = description.value.blob;
        }

        if (
            updateContent.body.product_id &&
            Object.keys(updateContent.body).length === 1
        ) {
            // Only product_id is set, no need to update.
            return;
        }

        syncStore.queueSync({
            entityType: EntityType.PRODUCT,
            args: [updateContent],
        });

        if (description.value?.isModified) {
            description.value.persist();
        }
        if (isNameModified.value) {
            persistName();
        }
    };
    const reset = async () => {
        await description.value?.resetContent();
        resetName();
    };

    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);
    registerFlush(() => throttledSave.flush());
    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) {
            throttledSave();
        }
    });

    refreshOnCrossTabEntitySync({
        ...externalRefresh,
        enabled: () => !!stek.value,
    });

    return {
        product,
        description,
        isFetching: computed(
            () => isFetching.value || isFetchingDescription.value
        ),
        isModified,
        reset,
    };
});

export const useProductManager = () => storeToRefs(useProductStore());
